/**
 * useReducer with DevTools Integration
 * 
 * Enhances React's useReducer with:
 * - Redux DevTools Extension integration
 * - Action logging in development
 * - State inspection
 * - Time-travel debugging
 * 
 * Usage:
 *   const [state, dispatch] = useReducerWithDevtools(
 *     reducer,
 *     initialState,
 *     'MyComponent'
 *   );
 */

import { useReducer, useEffect, useRef, Reducer as ReactReducer } from 'react';

import type { Reducer, Dispatch } from '@/types/state';

interface DevToolsExtension {
  connect(options?: { name: string }): DevToolsConnection;
}

interface DevToolsConnection {
  init(state: any): void;
  send(action: any, state: any): void;
  subscribe(listener: (message: any) => void): () => void;
}

declare global {
  interface Window {
    __REDUX_DEVTOOLS_EXTENSION__?: DevToolsExtension;
  }
}

/**
 * Enhanced useReducer hook with DevTools support
 */
export function useReducerWithDevtools<S, A>(
  reducer: Reducer<S, A>,
  initialState: S,
  debugName: string = 'Reducer'
): [S, Dispatch<A>] {
  const [state, dispatch] = useReducer(reducer as ReactReducer<S, A>, initialState);
  
  const devtools = useRef<DevToolsConnection | null>(null);
  const isDevToolsEnabled = typeof window !== 'undefined' && window.__REDUX_DEVTOOLS_EXTENSION__;

  // Initialize DevTools connection
  useEffect(() => {
    if (!isDevToolsEnabled) return;

    try {
      devtools.current = window.__REDUX_DEVTOOLS_EXTENSION__!.connect({
        name: debugName,
      });
      devtools.current.init(initialState);

      return () => {
        // Cleanup connection on unmount
        devtools.current = null;
      };
    } catch (error) {
      console.warn('[useReducerWithDevtools] Failed to connect to DevTools:', error);
    }
  }, [debugName, initialState, isDevToolsEnabled]);

  // Wrapped dispatch that logs to DevTools
  const dispatchWithDevtools: Dispatch<A> = (action: A) => {
    // Log to console in development
    if (import.meta.env.DEV) {
      console.group(`[${debugName}] ${(action as any).type || 'Action'}`);
      console.log('Action:', action);
      console.log('Previous State:', state);
    }

    // Dispatch the action
    dispatch(action);

    // Send to DevTools
    if (devtools.current) {
      try {
        devtools.current.send(action, reducer(state, action));
      } catch (error) {
        console.warn('[useReducerWithDevtools] Failed to send to DevTools:', error);
      }
    }

    if (import.meta.env.DEV) {
      console.log('Next State:', reducer(state, action));
      console.groupEnd();
    }
  };

  return [state, dispatchWithDevtools];
}

/**
 * Middleware wrapper for logging actions
 * 
 * Usage:
 *   const loggingReducer = withLogging(myReducer, 'MyReducer');
 *   const [state, dispatch] = useReducer(loggingReducer, initialState);
 */
export function withLogging<S, A>(
  reducer: Reducer<S, A>,
  debugName: string = 'Reducer'
): Reducer<S, A> {
  return (state: S, action: A): S => {
    if (import.meta.env.DEV) {
      console.group(`[${debugName}] ${(action as any).type || 'Action'}`);
      console.log('Action:', action);
      console.log('Previous State:', state);
    }

    const nextState = reducer(state, action);

    if (import.meta.env.DEV) {
      console.log('Next State:', nextState);
      console.groupEnd();
    }

    return nextState;
  };
}

/**
 * Middleware wrapper for performance monitoring
 * 
 * Logs reducer execution time in development.
 */
export function withPerformanceMonitoring<S, A>(
  reducer: Reducer<S, A>,
  debugName: string = 'Reducer'
): Reducer<S, A> {
  return (state: S, action: A): S => {
    if (!import.meta.env.DEV) {
      return reducer(state, action);
    }

    const start = performance.now();
    const nextState = reducer(state, action);
    const duration = performance.now() - start;

    if (duration > 16) {
      // Warn if reducer takes longer than one frame (16ms)
      console.warn(
        `[${debugName}] Slow reducer detected:`,
        `${(action as any).type || 'Action'} took ${duration.toFixed(2)}ms`
      );
    }

    return nextState;
  };
}

/**
 * Compose multiple middleware functions
 * 
 * Usage:
 *   const enhancedReducer = composeMiddleware(
 *     withLogging,
 *     withPerformanceMonitoring
 *   )(myReducer, 'MyReducer');
 */
export function composeMiddleware<S, A>(
  ...middlewares: Array<(r: Reducer<S, A>, name: string) => Reducer<S, A>>
) {
  return (reducer: Reducer<S, A>, debugName: string = 'Reducer'): Reducer<S, A> => {
    return middlewares.reduceRight(
      (acc, middleware) => middleware(acc, debugName),
      reducer
    );
  };
}
